import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { IIconProps } from '../Helpers/Common';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import NavigationLinks from './NavigationLinks';
import showLightNavBarOnPage from './NavigationUtils';

export enum Orientation {
	VERTICAL,
	HORIZONTAL,
}

export interface ILink extends IIconProps {
	shouldDisplay?: () => boolean;
	path: string;
	label: React.ReactNode;
	onClick?: (event?: any) => void;
	subLinks?: ILink[];
	subLinksFilter?: (link: ILink) => boolean;
	isDisabled?: boolean;
	useATag?: boolean;
	customComponent?: React.ReactNode;
	className?: string;
	innerTagClass?: string;
	showRedDot?: boolean;
}

export interface INavigationProps<T extends ILink> {
	className?: string;
	orientation: Orientation;
	linkGroups: Array<Array<T>>;
	filter?: (link: T) => boolean;
	alwaysExpanded?: boolean;
	isMobile?: boolean;
	isStaffManagerAdmin?: boolean;
	bookingsInCart?: boolean;
}

const Navigation = observer(<T extends ILink>(props: INavigationProps<T>) => {
	const location = useLocation();
	const [navCollapsed, setNavCollapsed] = React.useState(true);

	const alwaysExpanded = React.useMemo(() => {
		const { alwaysExpanded: alwaysExpandedProp, orientation } = props;
		if (orientation === Orientation.HORIZONTAL && alwaysExpandedProp === undefined) {
			return true;
		}
		return alwaysExpandedProp;
	}, [props.alwaysExpanded, props.orientation]);

	const getOrientationClassName = () => {
		const { orientation } = props;
		switch (orientation) {
			case Orientation.HORIZONTAL:
				return 'nav--horizontal';
			case Orientation.VERTICAL:
				return 'nav--vertical';
			default:
				break;
		}
		return '';
	};

	const onClickNavCollapse = () => {
		setNavCollapsed(!navCollapsed);
	};

	const getColorThemeClassName = () => {
		const { isMobile } = props;
		if (isMobile !== undefined) {
			if (showLightNavBarOnPage(isMobile, location)) {
				return 'nav--light';
			}
		}
		return 'nav--dark';
	};

	const { className, linkGroups, isStaffManagerAdmin } = props;

	let expandButton = null;
	let navClassName = classNames(className, 'nav', getOrientationClassName(), getColorThemeClassName());

	if (isStaffManagerAdmin !== undefined && isStaffManagerAdmin) {
		navClassName = classNames(navClassName, 'nav--staff');
	}

	if (!alwaysExpanded) {
		navClassName = classNames(navClassName, navCollapsed ? 'nav--collapsed' : 'nav--expanded');
		expandButton = (
			<button
				className={classNames('nav__expand-icon', 'link-rm-txt-dec', 'expand-icon', 'icon-menu', 'icon-only')}
				aria-label="Expand Menu"
				onClick={onClickNavCollapse}
			/>
		);
	}

	return (
		<nav className={navClassName}>
			{linkGroups.map((links, index) => {
				const key = `key-${index}`;
				return <NavigationLinks key={key} links={links} />;
			})}
			{expandButton}
		</nav>
	);
});

export default Navigation;
